import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { CloseIcon, Spinner } from '@/ui'

export interface PhotoEntry {
  key: string
  url: string
  caption: string
  comment?: string | null
}

interface PublicPhotoViewerProps {
  open: boolean
  photos: PhotoEntry[]
  index: number
  onIndexChange: (index: number) => void
  onClose: () => void
}

/**
 * In-page lightbox for inspections on the customer portal.
 * Renders a large photo with navigation, keyboard/touch support, and RTL awareness.
 */
export function PublicPhotoViewer({
  open,
  photos,
  index,
  onIndexChange,
  onClose,
}: PublicPhotoViewerProps) {
  const { t, i18n } = useTranslation()
  const scrimRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const prevButtonRef = useRef<HTMLButtonElement>(null)
  const nextButtonRef = useRef<HTMLButtonElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const touchStartXRef = useRef(0)
  const focusedElementRef = useRef<HTMLElement | null>(null)
  const isRTL = i18n.dir() === 'rtl'

  const stateRef = useRef({ onClose, onIndexChange, photos: photos || [], index, isRTL })
  useEffect(() => {
    stateRef.current = { onClose, onIndexChange, photos: photos || [], index, isRTL }
  }, [onClose, onIndexChange, photos, index, isRTL])

  useEffect(() => {
    if (!open) return

    // Store the currently focused element so we can restore it on close
    focusedElementRef.current = document.activeElement as HTMLElement
    document.body.style.overflow = 'hidden'

    // Focus the close button when dialog opens
    setTimeout(() => closeButtonRef.current?.focus(), 0)

    const handleKeydown = (e: KeyboardEvent) => {
      const { onClose, onIndexChange, photos, index, isRTL } = stateRef.current
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }

      // Focus trap: Tab cycles through buttons (close, prev, next)
      if (e.key === 'Tab') {
        const buttons = [closeButtonRef.current, prevButtonRef.current, nextButtonRef.current].filter(Boolean)
        if (buttons.length === 0) return

        const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement)
        let nextIndex = currentIndex + (e.shiftKey ? -1 : 1)
        if (nextIndex < 0) nextIndex = buttons.length - 1
        if (nextIndex >= buttons.length) nextIndex = 0

        e.preventDefault()
        buttons[nextIndex]?.focus()
        return
      }

      // In RTL, arrow directions are flipped: left moves forward, right moves backward
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        const nextIdx = isRTL ? index - 1 : index + 1
        if (nextIdx < photos.length && nextIdx >= 0) onIndexChange(nextIdx)
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        const prevIdx = isRTL ? index + 1 : index - 1
        if (prevIdx >= 0 && prevIdx < photos.length) onIndexChange(prevIdx)
      }
    }

    document.addEventListener('keydown', handleKeydown)
    return () => {
      document.removeEventListener('keydown', handleKeydown)
      document.body.style.overflow = ''
      // Restore focus to the element that opened the lightbox
      focusedElementRef.current?.focus()
    }
  }, [open])

  // Reset loading/error state when the current photo URL changes
  // This must happen BEFORE the return check so hook order is always consistent
  const currentUrl = photos[index]?.url
  useEffect(() => {
    setIsLoading(true)
    setHasError(false)
  }, [currentUrl])

  // Preload neighboring images
  useEffect(() => {
    if (!open || photos.length === 0) return
    const nextIdx = index + 1
    const prevIdx = index - 1
    if (nextIdx < photos.length && photos[nextIdx]) {
      new Image().src = photos[nextIdx]!.url
    }
    if (prevIdx >= 0 && photos[prevIdx]) {
      new Image().src = photos[prevIdx]!.url
    }
  }, [open, index, photos])

  if (!open || photos.length === 0) return null

  const photo = photos[index]!
  const hasPrev = index > 0
  const hasNext = index < photos.length - 1

  const handleImageLoad = () => {
    setIsLoading(false)
    setHasError(false)
  }

  const handleImageError = () => {
    setIsLoading(false)
    setHasError(true)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length > 0) {
      touchStartXRef.current = e.touches[0]!.clientX
    }
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    const { onIndexChange, photos, index, isRTL } = stateRef.current
    if (e.changedTouches.length === 0) return
    const touchEndX = e.changedTouches[0]!.clientX
    const diff = touchStartXRef.current - touchEndX
    const threshold = 50

    // Only handle horizontal swipes, ignore small movements
    if (Math.abs(diff) > threshold) {
      // In RTL, swipe directions are flipped
      const moveRight = diff < 0
      if ((isRTL && moveRight) || (!isRTL && !moveRight)) {
        // Swipe to next
        if (index < photos.length - 1) onIndexChange(index + 1)
      } else {
        // Swipe to previous
        if (index > 0) onIndexChange(index - 1)
      }
    }
  }

  const goToPrevious = () => {
    if (hasPrev) onIndexChange(index - 1)
  }

  const goToNext = () => {
    if (hasNext) onIndexChange(index + 1)
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    // Close only if click is outside the image, buttons, and caption area
    const shouldClose = !target.closest('img, button, [data-viewer-keep]')
    if (shouldClose) onClose()
  }

  return createPortal(
    <div
      ref={scrimRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('publicPortal.inspection.openPhoto', { label: photo.caption })}
      onClick={handleBackdropClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center"
    >
      {/* Close button */}
      <button
        ref={closeButtonRef}
        onClick={onClose}
        aria-label={t('publicPortal.inspection.close')}
        className="absolute top-4 end-4 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition"
      >
        <CloseIcon className="w-5 h-5" />
      </button>

      {/* Navigation buttons */}
      {hasPrev && (
        <button
          ref={prevButtonRef}
          onClick={goToPrevious}
          aria-label={t('publicPortal.inspection.previous')}
          className="absolute start-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {hasNext && (
        <button
          ref={nextButtonRef}
          onClick={goToNext}
          aria-label={t('publicPortal.inspection.next')}
          className="absolute end-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Main image container */}
      <div className="relative flex-1 flex items-center justify-center w-full px-4">
        {isLoading && (
          <div className="flex items-center justify-center">
            <Spinner size="md" className="text-white" />
          </div>
        )}

        {hasError ? (
          <div className="text-center text-white space-y-2">
            <p className="text-body-md">{t('publicPortal.inspection.photoFailed')}</p>
          </div>
        ) : (
          <img
            key={photo.url}
            ref={imageRef}
            src={photo.url}
            alt={photo.caption}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ touchAction: 'pan-y pinch-zoom' }}
            className="max-h-[calc(100vh-200px)] max-w-[calc(100vw-32px)] object-contain"
          />
        )}
      </div>

      {/* Caption and comment */}
      <div className="w-full px-4 py-4 bg-black/50 text-white text-center space-y-2" data-viewer-keep>
        <div className="text-body-sm">{photo.caption}</div>
        {photo.comment && <div className="text-label-sm text-gray-300">{photo.comment}</div>}
        <div className="text-label-sm text-gray-400">
          {t('publicPortal.inspection.photoOf', { current: index + 1, total: photos.length })}
        </div>
      </div>
    </div>,
    document.body,
  )
}
